import type { HeadsUpIntroConfig } from './headsUpIntroConfig';
import type { MultiRunIntroConfig } from './multiRunIntroConfig';

/**
 * The geolocation implementations a client can run. Declared as a union rather
 * than a bare string so an unhandled variation is a compile error at every
 * branch rather than a silent fallthrough at runtime.
 */
export const GEOFENCE_PATHS = ['lexisnexis', 'legacy', 'legacy-shadow'] as const;
export type GeofencePath = (typeof GEOFENCE_PATHS)[number];

/**
 * A LaunchDarkly string flag holds whatever an operator typed into it, so an
 * unrecognised value has to mean something defined. It means the compiled
 * default, `legacy-shadow`: the one value that is safe under every server
 * setting. It used to mean `lexisnexis`, which runs no legacy check at all, so
 * under `provider=legacy` -- where the server enforces nothing -- a typo'd
 * variation admitted every player it reached with no location check.
 */
export const normalizeGeofencePath = (raw: unknown): GeofencePath =>
  GEOFENCE_PATHS.includes(raw as GeofencePath)
    ? (raw as GeofencePath)
    : FEATURE_FLAG_DEFAULTS.geofencePath;

export const FEATURE_FLAGS = {
  // EN-11003. Owner: Roland. Remove under EN-11003 within two weeks of full rollout.
  reduceAnimations: 'release.reduce-animations',
  // EN-10795. Owner: Roland. Remove under EN-10795 within two weeks of
  // full production rollout. Off preserves the existing tournament UI.
  sngPayoutStructure: 'release.sng-payout-structure',
  performanceHud: 'ops.client-performance-hud',
  systemLog: 'ops.client-system-log',
  // The one-time multi-run introduction (EN-10791). A JSON flag whose
  // variation IS the announcement — `off` (null), `nlhe-launch`, then
  // `omaha-live` — each edition with its own seen key, switched in
  // LaunchDarkly with no deploy (multiRunIntroConfig.ts). An announcement,
  // not a rollout tool: multi-run itself is enabled per table template.
  // Owner: Jason Dijols. Expires two weeks after the last edition reaches
  // 100% of production players; removal is EN-10947.
  multiRunIntro: 'release.multi-run-intro',
  // The one-time heads-up Sit & Go introduction (EN-10748), on the same
  // pattern: a JSON flag whose variation IS the announcement -- `off` (null)
  // or an edition such as `heads-up-launch` (headsUpIntroConfig.ts). While an
  // edition is live the Sit & Go tab wears a NEW badge as well. An
  // announcement, not a rollout tool: heads-up tables exist per template.
  // Owner: Jason Dijols. Expires two weeks after the last edition reaches 100%
  // of production players; removal is EN-11315.
  headsUpIntro: 'release.heads-up-intro',
  // EN-10921: withhold RUN IT 3X from the multi-run offer without a release.
  // Operational, so it is retained: the lever for Ryan's one-month 3X review.
  // On, an initiator is offered 1X and 2X only, and the felt line says so, from
  // the next render on every table. Off is today's 1X / 2X / 3X. Client-side:
  // the vote writers still accept a 3 from a client that has not updated.
  multiRunWithhold3x: 'ops.multi-run-withhold-3x',
  // EN-10747: the Sit & Go tab's seat toggle — Heads-up and Six-seat, each on
  // or off by itself (EN-11104: both on to start), with Hide Full beside them,
  // filtering the tab by seat count; the filter drawer's Sit & Go Table Size is
  // the same toggle. On
  // once two-seat tables exist (EN-10745); off is today's tab, with no filter
  // row, every sit & go shown whatever its seat count, and the drawer's three
  // sizes filtering it. Owner: Kwing Mabanag. Expires
  // two weeks after it reaches 100% of production players; removal ticket to be
  // filed with the rollout.
  sitgoSeatToggle: 'release.sit-go-seat-toggle',
  // EN-11109: the Sit & Go end-of-game result modal — the won / lost title, the
  // amount, "Sit & Go finished", and the "Play Again?" rematch offer,
  // the felt's Go to lobby chip, and the exits landing on the lobby's Sit & Go
  // tab. Off is today's shared "Tournament has finished" screen and Exit Table
  // to Cash. MTT is never affected. Owner: Kwing Mabanag. Expires two weeks
  // after it reaches 100% of production players; removal ticket to be filed
  // with the rollout.
  sitGoResultModal: 'release.sit-go-result-modal',
  // EN-11139: the Sit & Go tab laid out like the cash tab — one section per
  // buy-in with the cash heading, tables ordered partial / one empty / full by
  // format, "Heads-up" / "Six-seat" in each card's name slot (and as the title
  // of the detail modal the card opens, EN-11306). Layout only: the filter
  // stays sitgoSeatToggle's either way. Off is today's prize-pool ladder.
  // Owner: Kwing Mabanag.
  // Expires two weeks after it reaches 100% of production players; removal
  // ticket to be filed. Removal also removes SitGoLadder.
  sitGoBuyInLobby: 'release.sit-go-buy-in-lobby',
  // EN-11257: a Rolling Sit & Go before it starts — registrants drawn in their
  // seats (avatar, name, starting stack) for every viewer, the felt rotated to
  // the viewer's own drawn seat, and a waiting panel on the felt (open seats,
  // buy-in, prize; Unregister and Tournament details for a registrant) in
  // place of the Sit & Go pre-start modal, which it retires. The panel's
  // Unregister replaces the modal's, so the two must ship under this one flag.
  // Off is today's Open seats and pre-start modal. MTT is never affected.
  // Owner: Kwing Mabanag. Expires two weeks after it reaches 100% of
  // production players; removal ticket pending (to be filed with the Sit & Go
  // flag-removal ticket).
  sitGoSeatedWaiting: 'release.sit-go-seated-waiting',
  /**
   * Which geolocation implementation this client runs.
   *
   *   lexisnexis    the vendor path alone. The client gets a Radar verdict,
   *                 posts the signed token, and the server decides. Shipped
   *                 behaviour and the default.
   *   legacy        the pre-LexisNexis path alone. The client emits
   *                 `trackLocation` over the socket AFTER connecting and raises
   *                 its own modal from `gotLocation`. No server-side geofence.
   *   legacy-shadow both. Legacy owns what the player sees, exactly as under
   *                 `legacy`; the vendor check runs alongside it purely so the
   *                 server records what LexisNexis WOULD have decided. Nothing
   *                 acts on that verdict.
   *
   * REPLACES THE BOOLEAN `ops.client-geofence-legacy`. A LaunchDarkly flag's
   * type is fixed at creation, so a third variation needs a new key rather than
   * an edit. Retire the boolean once every environment is serving this one;
   * leaving both live is how two switches start disagreeing.
   *
   * `ops.` rather than `migrate.` deliberately. ADR-0015 reserves `migrate.`
   * for old-to-new selection that is removed once the old path is deleted; this
   * is the other direction -- a long-lived operational control that outlives
   * the migration and is meant to be exercised periodically.
   *
   * The server half of this switch is the SSM parameter /<stage>/geo/provider.
   * They must be moved in the documented order (see
   * set-geo-enforcement-mode.yml): server to legacy FIRST, then this flag off
   * the vendor path; back to the vendor path FIRST, then server to lexisnexis.
   * A client on `legacy` or `legacy-shadow` writes no verdict the server will
   * admit on, so with `provider=lexisnexis` and `mode=enforce` those players
   * are refused at the websocket with `no_login_verdict`.
   *
   * SHADOW REQUIRES `provider=legacy`. A shadow run stores a verdict like any
   * other, so under `provider=lexisnexis` the socket would start enforcing the
   * very answer shadow exists to leave unenforced. Keep `mode=observe` too, so
   * a mistaken provider flip still refuses nobody.
   */
  geofencePath: 'ops.client-geofence-path',
  // EN-10960: the in-table Tournament Info panel's Add-On row — the add-on's
  // fee and chips, so a seated player knows what to fund before the short
  // add-on break. Off is today's panel, which shows only the add-on count.
  // Owner: Roland. Expires two weeks after it reaches 100% of production
  // players; removal ticket to be filed with the rollout.
  tournamentAddOnInfo: 'release.tournament-addon-info',
  // EN-11054. Owner: referrals-api maintainers. Retained operational control
  // for the whole program, targeted by referrer GUID in each environment.
  // Menu visibility only. Backend operations use database settings.
  referrals: 'ops.referrals',
  // EN-10912: an explicit checkbox on the pre-action and Post Blind / Wait for
  // Big Blind chips, ticked when selected (the old Unity client's checkmark).
  // Off is today's colour-only selected state. Visual only: the chips' values
  // and click behaviour are the same either way, so a mid-hand flip is safe.
  // Owner: Neil Villareal. Expires two weeks after it reaches 100% of
  // production players; removal is EN-11061.
  preactionCheckboxIndicator: 'release.preaction-checkbox-indicator',
  // EN-10885: the cash lobby's In Play stat. Retained, so product can bring the
  // stat back, or take it off again, without a release. On shows it only while
  // cash in play is over the $200,000 floor (ActivityStrip). Off hides it at
  // any amount, as the story's acceptance criteria require: this flag's off
  // path is the stat withheld, not the always-shown stat that came before it.
  lobbyCashInPlay: 'ops.lobby-cash-in-play',
  // EN-11031: when a live board is missing from client state past its grace,
  // say so on the felt ("Board not received") and offer the player a bounded
  // Reconnect (never automatic) for a fresh snapshot (Table/boardDeficit).
  // Off is today's blank board; the board_short_on_felt report is sent either
  // way. Owner: Roland. Expires two weeks after it reaches 100% of production
  // players; removal ticket to be filed with the rollout.
  boardMissingRecovery: 'release.board-missing-recovery',
  // EN-11016: the tournament calendar shows every later occurrence of each
  // recurring tournament, from today out to 30 days, as display-only tiles. Off is the schedule exactly as the
  // engine sends it. Owner: Neil Villareal. Expires two weeks after it reaches
  // 100% of production players; removal ticket to be filed with the rollout.
  tournamentRecurringSchedule: 'release.tournament-recurring-schedule',
  // EN-11178: report a table frame that arrives out of order, and a table whose
  // valve opens, to Pendo (and to Sentry where a DSN is set; no shipped build
  // today) (utils/frameOrderReport.ts; at most one per table per hand, 20
  // reports per session in all). Detection and the QA HUD count run either way,
  // on this device only; this switches the reporting, so report volume is an
  // operator's call. Off sends nothing, as before. Operational: kept while out-of-order frames are being
  // measured for the dropper and PACECHAIN rollouts. Owner: Kwing Mabanag.
  frameOrderReport: 'ops.frame-order-report',
  // EN-11178: drop a table frame that is not newer than the newest one already
  // seen for its table (utils/frameOrder.ts), so a late delivery cannot re-show
  // a state the table has left. Off is today's behaviour: every frame is
  // applied in arrival order. Safe to flip at any frame: the newest mark is
  // kept either way. Gates PACECHAIN on live. Owner: Kwing Mabanag. Expires two
  // weeks after it reaches 100% of production players; removal ticket to be
  // filed with the rollout. That removal also deletes the broadcaster's
  // `payloadVersion` and the currentTable reducer's `version` guard, so
  // frameVersion is the one order token.
  tableFrameOrder: 'release.table-frame-order'
} as const;

export type FeatureFlagName = keyof typeof FEATURE_FLAGS;

/** Each flag's value: a boolean, unless the flag carries a payload. */
export type FeatureFlagValues = {
  reduceAnimations: boolean;
  sngPayoutStructure: boolean;
  performanceHud: boolean;
  systemLog: boolean;
  multiRunIntro: MultiRunIntroConfig | null;
  headsUpIntro: HeadsUpIntroConfig | null;
  multiRunWithhold3x: boolean;
  sitgoSeatToggle: boolean;
  sitGoResultModal: boolean;
  sitGoBuyInLobby: boolean;
  sitGoSeatedWaiting: boolean;
  geofencePath: GeofencePath;
  tournamentAddOnInfo: boolean;
  referrals: boolean;
  preactionCheckboxIndicator: boolean;
  lobbyCashInPlay: boolean;
  boardMissingRecovery: boolean;
  tournamentRecurringSchedule: boolean;
  frameOrderReport: boolean;
  tableFrameOrder: boolean;
};

// Off must always preserve the behavior that existed before the flag.
export const FEATURE_FLAG_DEFAULTS: FeatureFlagValues = {
  reduceAnimations: false,
  sngPayoutStructure: false,
  performanceHud: false,
  systemLog: false,
  multiRunIntro: null,
  headsUpIntro: null,
  multiRunWithhold3x: false,
  sitgoSeatToggle: false,
  sitGoResultModal: false,
  sitGoBuyInLobby: false,
  sitGoSeatedWaiting: false,
  // THE SHIPPING CONFIGURATION, because this default is what a large share of
  // sessions actually run. `useFeatureFlag` returns it until the LaunchDarkly
  // client resolves, and it never resolves at all when there is no identity or
  // no client-side id -- so the default is not an edge case, it is the common
  // one, and it should be the state we are releasing in.
  //
  // Shadow is safe under BOTH server settings, which is the property the old
  // default was chosen for and this one keeps. A shadow client still runs the
  // vendor check and still POSTs the verdict, so it is admitted whether the
  // provider dial reads `legacy` or `lexisnexis`. Plain `legacy` would not be:
  // it writes no verdict, so against a server expecting one it is refused with
  // `no_login_verdict`.
  //
  // It also fails in the kinder direction for a client that cannot reach
  // LaunchDarkly at all: legacy owns the screen, so that player gets the
  // behaviour the product had before any of this existed.
  geofencePath: 'legacy-shadow',
  tournamentAddOnInfo: false,
  referrals: false,
  preactionCheckboxIndicator: false,
  lobbyCashInPlay: false,
  boardMissingRecovery: false,
  tournamentRecurringSchedule: false,
  frameOrderReport: false,
  tableFrameOrder: false
};

/** What telemetry records for a flag's value: the variant, and the path it selects. */
export type FeatureFlagPath = { variant: string; path: string };

/**
 * Where a player reaches a flag's decision, as `feature_flag_exposure` records
 * it. A closed list so Pendo reports never split one surface across spellings.
 */
export type FeatureFlagSurface =
  | 'settings'
  | 'multi_run_vote'
  | 'account_menu'
  | 'sit_go_result'
  | 'sit_go_lobby'
  | 'tournament_calendar' | 'table'
  | 'sit_go_waiting';

const OFF: FeatureFlagPath = { variant: 'off', path: 'hidden' };
const AVAILABLE: FeatureFlagPath = { variant: 'on', path: 'available' };

/**
 * The variant and code path each value selects, for Sentry. Never the payload:
 * a JSON flag reports its edition id and nothing else.
 */
export const FEATURE_FLAG_PATHS: {
  [Flag in FeatureFlagName]: (value: FeatureFlagValues[Flag]) => FeatureFlagPath;
} = {
  reduceAnimations: (enabled) => (enabled ? AVAILABLE : OFF),
  sngPayoutStructure: (enabled) => (enabled ? AVAILABLE : OFF),
  performanceHud: (enabled) => (enabled ? AVAILABLE : OFF),
  systemLog: (enabled) => (enabled ? AVAILABLE : OFF),
  multiRunIntro: (config) => (config ? { variant: config.edition, path: config.edition } : OFF),
  headsUpIntro: (config) => (config ? { variant: config.edition, path: config.edition } : OFF),
  multiRunWithhold3x: (withheld) =>
    withheld ? { variant: 'on', path: 'withhold-3x' } : { variant: 'off', path: 'offer-3x' },
  sitgoSeatToggle: (enabled) =>
    enabled ? { variant: 'on', path: 'seat-toggle' } : { variant: 'off', path: 'all-seats' },
  sitGoResultModal: (enabled) =>
    enabled
      ? { variant: 'on', path: 'sit-go-result' }
      : { variant: 'off', path: 'tournament-finished' },
  sitGoBuyInLobby: (enabled) =>
    enabled
      ? { variant: 'on', path: 'buy-in-sections' }
      : { variant: 'off', path: 'prize-pool-ladder' },
  sitGoSeatedWaiting: (enabled) =>
    enabled
      ? { variant: 'on', path: 'seated-waiting' }
      : { variant: 'off', path: 'pre-start-modal' },
  // Names the implementation rather than on/off, because that is what the
  // reader of a Sentry trace needs to know: which geolocation path produced the
  // error they are looking at. `legacy-shadow` is reported as itself and not
  // collapsed into `legacy`, since the two differ in whether a vendor call was
  // made and that is exactly what a cost or vendor-error trace is asking.
  geofencePath: (path) => ({ variant: path, path }),
  tournamentAddOnInfo: (enabled) => (enabled ? AVAILABLE : OFF),
  referrals: (enabled) =>
    enabled ? { variant: 'on', path: 'published-fee-progress' } : OFF,
  preactionCheckboxIndicator: (shown) =>
    shown ? { variant: 'on', path: 'checkbox' } : { variant: 'off', path: 'colour-only' },
  lobbyCashInPlay: (enabled) =>
    enabled ? { variant: 'on', path: 'above-floor' } : { variant: 'off', path: 'hidden' },
  boardMissingRecovery: (enabled) =>
    enabled ? { variant: 'on', path: 'recover' } : { variant: 'off', path: 'blank-board' },
  tournamentRecurringSchedule: (enabled) =>
    enabled
      ? { variant: 'on', path: 'recurring-projected' }
      : { variant: 'off', path: 'published-only' },
  frameOrderReport: (enabled) =>
    enabled ? { variant: 'on', path: 'report' } : { variant: 'off', path: 'count-only' },
  tableFrameOrder: (enabled) =>
    enabled ? { variant: 'on', path: 'drop-stale' } : { variant: 'off', path: 'arrival-order' }
};
